import { v4 } from "uuid";

export type Severity = "debug" | "info" | "warn" | "error";

/**
 * Why is this happening?  We default to SERVER_ERROR.
 * The rest log as warn by default
 */
export type ErrorType =
  | "BAD_USER_INPUT"
  | "SERVER_ERROR"
  | "UNAUTHENTICATED"
  | "FORBIDDEN";

/**
 *
 * @typedef {object} FieldValidationError
 * @property {string} message - an error message
 * @property {string} path - points to the location of the problem
 * @property {string[]} errors - array of what is wrong.
 *
 */
export type FieldValidationError = Translatable & {
  message?: string;
  path?: string;
  // input validation errors prefer this.
  errors?: string[];
};

/**
 *
 * @typedef {object} Translatable
 * @property {string} translation - use of this wasn't clear in the past.  Some put translation keys here, some values.  Do not use.
 * @property {string} translationKey - key in our json content files.
 *  When used in errors, this can define the error for robot consumers.
 *  e.g. api.directory.person.managerMayNotBeSelf  Automatically translated by GQL middleware.
 * @property {string} translationValues - Values that get passed into the translation function.
 *
 */
export type Translatable = {
  /**
   * @deprecated you probably want 'translationKey' if this is a key.  GQL middleware handles actually doing the translation.
   */
  translation?: string;
  translationKey?: string;
  translationValues?: { [key: string]: any };
};

/**
 * Basic error metadata
 *
 */
export type ErrorMetadata = Translatable & {
  errorType?: ErrorType;
  serviceName?: string;
  severity?: Severity;
  // Used in batch operations to indicate that the error is stored in the db and exposed in the graph.
  apiRecordedError?: boolean;
  error?: { message: string; stack?: string };
  fields?: FieldValidationError[];
  [key: string]: unknown;
};

/**
 * Defines an interface that can be used to pass around error types.
 *
 * Useful to pass in the error to a function when you sometimes want a basic KazooError and sometimes want NotFoundError or UserInputError
 *
 * {@link @kazoohr/helpers/src/assertIsNotNullOrUndefined.ts}
 */
export interface IKazooErrorConstructable<TMetadata extends object = {}> {
  new (
    message: string,
    metadata?: TMetadata & ErrorMetadata
  ): KazooError<TMetadata>;
}

/**
 * Helper function that lets us new up a Kazoo error of an arbitrary type
 */
export function initializeError<TMetadata extends object & ErrorMetadata>(
  error: IKazooErrorConstructable<TMetadata>,
  message: string,
  metadata?: TMetadata
) {
  return new error(message, metadata);
}

/**
 * Basic structured error that accepts a blob of metadata.
 *
 * Stick whatever you want in the metadata blob and it will be serialized with logs.
 *
 * Code and extensions make it compatible with ApolloErrors for validation.
 */
export class KazooError<TMetadata extends object = {}> extends Error {
  readonly errorType: ErrorType;
  readonly severity: Severity | undefined;
  readonly id: string;
  readonly metadata: TMetadata & ErrorMetadata;

  constructor(
    message: string,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    metadata = {} as TMetadata & ErrorMetadata
  ) {
    super(message);

    this.metadata = metadata;
    this.id = v4();
    this.name = "KazooError";
    this.errorType = this.metadata?.errorType || "SERVER_ERROR";
    if (this.metadata?.severity) {
      this.severity = this.metadata.severity;
    } else {
      if (this.errorType === "SERVER_ERROR") {
        this.severity = "error";
      } else {
        this.severity = "warn";
      }
    }
  }

  get originalError() {
    return this.metadata.error;
  }

  get extensions() {
    const {
      serviceName,
      translationKey,
      translationValues,
      fields,
      errorType = "SERVER_ERROR",
      ...metadata
    } = this.metadata;

    return {
      code: errorType,
      metadata,
      serviceName,
      translationKey,
      translationValues,
      validation: fields,
    };
  }

  /**
   * Capture the real Error from a different scope as metadata,
   * preserving the call stack where the KazooError is constructed.
   *
   * @example
   *
   * const domainError = new KazooError("My thing failed.")
   * setTimeout(() => {
   *   const errorWithBadStack = new Error("boo!");
   *   domainError.captureOriginalError(errorWithBadStack);
   * }, 100);
   */
  captureOriginalError(error: Error) {
    this.metadata.error = error;
  }

  toJSON() {
    const { message, id, metadata, stack } = this;

    const originalError = this.originalError && {
      message: this.originalError.message,
      stack: this.originalError.stack,
    };

    return {
      message,
      stack,
      id,
      ...metadata,
      // This ensures the actual error appears in the logs. Otherwise it would just be an empty object.
      originalError,
    };
  }
}

/**
 * Reinventing the HTTP 404 Not Found wheel.
 */
export class NotFoundError extends KazooError {
  constructor(message: string, metadata?: any) {
    super(message, { ...metadata, errorType: "NOT_FOUND" });
  }
}

/**
 * Reinventing the HTTP 400 BAD REQUEST wheel.
 */
export class UserInputError<TMetadata extends object = {}> extends KazooError {
  constructor(message: string, metadata?: TMetadata & ErrorMetadata) {
    super(message, { ...metadata, errorType: "BAD_USER_INPUT" });
  }
}

/**
 * Reinventing the HTTP 403 FORBIDDEN wheel.
 */
export class ForbiddenError<TMetadata extends object = {}> extends KazooError {
  constructor(message: string, metadata?: TMetadata & ErrorMetadata) {
    super(message, { ...metadata, errorType: "FORBIDDEN" });
  }
}

/**
 * Reinventing the HTTP 401 UNAUTHORIZED wheel.
 */
export class NotAuthenticatedError<
  TMetadata extends object = {}
> extends KazooError {
  constructor(message: string, metadata?: TMetadata & ErrorMetadata) {
    super(message, { ...metadata, errorType: "UNAUTHENTICATED" });
  }
}

/**
 * If error is a KazooError, return the translationKey and translationValue from the metadata
 * @param error it might be a KazooError
 * @param defaultTranslationKey if it isn't a KazooError, what key do we pass back
 * @returns {translationKey, translationValues}
 */
export function getTranslationKeyAndValuesFromKazooErrorIfPossible(
  error: any,
  defaultTranslationKey: string
) {
  return {
    translationKey:
      ("metadata" in error && error.metadata?.translationKey) ||
      defaultTranslationKey,
    translationValues:
      ("metadata" in error && error.metadata?.translationValues) || {},
  };
}

/**
 *  Returns true if the error has the translation key
 * @param error  an error
 * @param key a key
 * @returns boolean
 */
export function hasTranslationKey(error: unknown, key: string) {
  if (
    error &&
    typeof error === "object" &&
    "metadata" in error &&
    error.metadata &&
    typeof error.metadata == "object" &&
    "translationKey" in error.metadata &&
    error.metadata.translationKey === key
  ) {
    return true;
  }

  // cross schema GQL error
  if (
    error &&
    typeof error === "object" &&
    "extensions" in error &&
    error.extensions &&
    typeof error.extensions == "object" &&
    "translationKey" in error.extensions &&
    error.extensions.translationKey === key
  ) {
    return true;
  }

  return false;
}
